import React, {
  useEffect, useMemo, useCallback, useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { NoNotificationsText } from 'components/BellNotification/BellNotificationStyled';
import NotificationList from 'components/BellNotification/NotificationList/NotificationList';
import { ViewAllLink } from 'components/BellNotification/NotificationList/NotificationListStyled';
import { connect } from 'react-redux';
import {
  getUserNotifications,
  markNotificationAsRead,
} from 'actions/notificationsActions';
import { KIND } from 'api/NotificationApi';
import { isSpecialTaskEvent } from 'reducers/notificationsReducer';
import Localize from 'components/Localize';
import { roles, actions } from 'utils/access-rules/constants';
import AbilitiesService from 'utils/access-rules/abilities';
import { section } from 'utils/access-rules/rules/notifications';
import { filterByInternship } from 'reducers/reducersUtils';
import { StyledBellNotification } from './NotificationStyled';

const Notification = ({
  getUserNotifications,
  markNotificationAsRead,
  notifications,
  role,
  isUserDropdownOpen,
  isDropdownOpen,
  onVisibilityChange,
  selectedInternshipId,
  notificationsLoading,
}) => {
  const [limit, setLimit] = useState(10);
  const stableGetUserNotifications = useCallback(
    (limit) => getUserNotifications(limit),
    [getUserNotifications],
  );
  useEffect(() => {
    if (notificationsLoading) return;
    stableGetUserNotifications(limit);
    // `notificationsLoading` bewusst NICHT in den deps: der Effekt soll bei
    // limit-Aenderung neu laden, aber NICHT bei jedem Loading-Toggle — sonst
    // entstuende ein Fetch-Loop (vgl. OPT-6-Incident). `limit` ist drin, damit
    // "load more" reaktiv nachlaedt.
  }, [stableGetUserNotifications, limit]);

  const onLoadMore = () => {
    // onLoadMore wird per Infinite-Scroll (NotificationList.handleScroll) bei
    // JEDEM Scroll-to-bottom gefeuert — ohne Debounce/Disable. Waehrend eines
    // laufenden Fetches daher NICHT erneut anstossen: sonst zaehlt `limit` ueber
    // die geladene Menge hinaus und der useEffect verschluckt die naechste Seite
    // (er returnt bei notificationsLoading) -> Liste bleibt haengen. Erst nach
    // Loading-Ende laedt der naechste Scroll die naechste Stufe.
    if (notificationsLoading) return;
    // Funktionales Update -> kein stale `limit` bei mehreren Scroll-Events.
    setLimit((prevLimit) => prevLimit + 10);
  };

  const getLink = (notification) => {
    if (role === roles.Applicant) {
      if (
        notification.kind === KIND.CALENDAR_EVENT
        || notification.kind === KIND.INVITEE_CHANGED_STATUS
      ) {
        // Termin-PN (SuS, CALENDAR_EVENT/INVITEE_CHANGED_STATUS) -> Applicant-Kalender direkt im Termin-Monat oeffnen: ?month=YYYY-MM
        // aus event_start anhaengen. Monat per moment.utc ableiten (Backend speichert/filtert
        // start nach TIME_ZONE=UTC; lokales moment() driftet an Monatsgrenzen). KEIN #calendar-
        // Hash — auf /calendar ist der Kalender die ganze Seite (kein Scroll noetig). Ohne
        // event_start -> Pfad unveraendert ohne ?month=. LOGISCH IDENTISCH zu
        // resolveNotificationLink.js (gespiegelte Kopie), nur ohne den #calendar-Hash des
        // non-Applicant-Zweigs.
        const path = selectedInternshipId ? `/calendar/${selectedInternshipId}` : '/calendar';
        const month = notification.event_start
          ? moment.utc(notification.event_start).format('YYYY-MM')
          : null;
        return month ? `${path}?month=${month}` : path;
      }
      if (
        notification.kind === KIND.CHAT_MESSAGE
        || notification.kind === KIND.CHAT_MESSAGE_HAS_BEEN_READ
      ) {
        return selectedInternshipId ? `/chat/${selectedInternshipId}` : '/chat';
      }
      if (notification.kind === KIND.CONTRACT_UPLOADED) {
        return '/applicant-documents';
      }
      if (isSpecialTaskEvent(notification.kind)) {
        return selectedInternshipId
          ? `/applicant/${selectedInternshipId}`
          : '/applicant';
      }
      if (notification.kind === KIND.INTERNSHIP_REPORT_UNSUBMITTED) {
        return selectedInternshipId
          ? `/internship-report/${selectedInternshipId}`
          : '/internship-report';
      }
      if (notification.kind === KIND.TEACHER_ASSESSMENT_UNSUBMITTED) {
        return selectedInternshipId
          ? `/your-assessment/${selectedInternshipId}`
          : '/your-assessment';
      }
      if (
        notification.kind === KIND.TEACHER_ADDED_OPERATIONAL_LEARNING_TASK
        || notification.kind === KIND.TEACHER_UNSUBMITTED_OLT_REPORT
      ) {
        return selectedInternshipId
          ? `/learning-task/${selectedInternshipId}`
          : '/learning-task';
      }
    } else {
      // role != Applicant (Lehrkraft / CompanyEmployee / EducationProvider).
      //
      // OBERSTES ZIEL: NIE undefined zurückgeben => nie 404 — UND nur Ziele auflösen, die
      // ALLE Nicht-Applicant-Rollen wirklich erreichen dürfen. Hintergrund (adversarialer
      // Review): App.jsx wrappt jede Route in <CanRoute to={page.X}>; fehlt der Rolle das
      // Recht, greift <Redirect to="/"> (CanRoute.jsx). In routes.js sind universell
      // (Teacher + CompanyEmployee + EducationProvider) NUR page.ApplicantProfile
      // (/applicant-profile/...) und page.Applicants (/applicants). NICHT universell:
      // /calendar (Applicant-only) und /manage-students (CompanyEmployee fehlt das Recht).
      // Daher hier ausschließlich die zwei universellen Ziele. LOGISCH IDENTISCH zu
      // resolveNotificationLink.js (gespiegelte Kopie).
      if (
        notification.kind === KIND.CHAT_MESSAGE
        || notification.kind === KIND.CHAT_MESSAGE_HAS_BEEN_READ
      ) {
        // Chat-Deeplink — Ziel-Pfad (/applicant-profile/...) unverändert, ABER der Guard
        // wurde auf applicant && sender verschärft und der Fallback führt jetzt auf
        // /applicants (statt zuvor /manage-students), damit das Ziel immer erreichbar ist.
        if (notification.applicant && notification.sender) {
          return notification.internship
            ? `/applicant-profile/${notification.applicant.pk}/${notification.internship.pk}?chat-id=${notification.sender.pk}#chat`
            : `/applicant-profile/${notification.applicant.pk}?chat-id=${notification.sender.pk}#chat`;
        }
        return '/applicants';
      }
      // CALENDAR_EVENT/INVITEE_CHANGED_STATUS -> der Termin gehört in den Kalender, nicht
      // auf die Bewerberliste/das Profil. Der "IHR TERMINKALENDER" (ScheduleCalendar) lebt
      // auf der /applicants-Route (UserManagerPage, id="calendar") -> Hash-Deeplink
      // /applicants#calendar; UserManagerPage scrollt nach Lade-Ende per react-scroll dahin.
      // /applicants ist rollen-universell erreichbar (anders als /calendar = Applicant-only).
      // Mit event_start (CalendarEvent.start vom Backend) den Kalender direkt im Termin-Monat
      // öffnen: ?month=YYYY-MM als Query VOR dem Hash (sonst verschluckt das Hash-Fragment den
      // Param). Monat per moment.UTC ableiten: das Backend speichert/filtert start nach
      // TIME_ZONE=UTC (CalendarEventMonthFilter -> start__month), und der Kalender lädt
      // getEvents(month). Lokales moment() würde an Monatsgrenzen driften (Termin 30.6. 22:00 UTC
      // wäre in Berlin Juli -> month=07 -> Backend lädt UTC-Juni -> Termin unsichtbar). Steht
      // bewusst VOR der applicant-Profil-Logik. LOGISCH IDENTISCH zu resolveNotificationLink.js.
      if (
        notification.kind === KIND.CALENDAR_EVENT
        || notification.kind === KIND.INVITEE_CHANGED_STATUS
      ) {
        const month = notification.event_start
          ? moment.utc(notification.event_start).format('YYYY-MM')
          : null;
        return month ? `/applicants?month=${month}#calendar` : '/applicants#calendar';
      }
      if (notification.applicant && notification.applicant.pk) {
        return notification.internship
          ? `/applicant-profile/${notification.applicant.pk}/${notification.internship.pk}`
          : `/applicant-profile/${notification.applicant.pk}`;
      }
      return '/applicants';
    }
    return undefined;
  };
  const unread = notifications.filter(({ read_at }) => read_at == null);

  const handleBellClose = () => {
    // mark as read on close to be able user see which one in unread during opens.
    Promise.all(unread.map(({ pk }) => markNotificationAsRead(pk)));
  };

  const stableGetLink = useCallback(getLink, [notifications]);
  const notificationsWithLink = useMemo(
    () => notifications.map((notification) => ({
      ...notification,
      to: stableGetLink(notification),
    })),
    [stableGetLink, notifications],
  );

  return (
    <StyledBellNotification
      hasUnread={unread.length > 0}
      onClose={handleBellClose}
      isUserDropdownOpen={isUserDropdownOpen}
      isDropdownOpen={isDropdownOpen}
      onVisibilityChange={onVisibilityChange}
    >
      {(close) => notifications.length > 0 ? (
        <NotificationList
          loading={notificationsLoading}
          userRole={role}
          notifications={notificationsWithLink}
          onClick={close}
          markNotificationAsRead={markNotificationAsRead}
          onLoadMore={onLoadMore}
        />
      ) : (
        <>
          <NoNotificationsText>
            <Localize id="you-dont-have-any-notifications" />
          </NoNotificationsText>
          {/* B8-Nachtrag: "Alle anzeigen" auch im Leer-Zustand erreichbar (fuer
              Lehrkraefte am verstaendlichsten = immer da). Gleicher token-basierter
              ViewAllLink wie in NotificationList; schliesst das Dropdown via close. */}
          <ViewAllLink to="/notifications-overview" onClick={close}>
            <Localize id="view-all-notifications" />
          </ViewAllLink>
        </>
      )}
    </StyledBellNotification>
  );
};

Notification.propTypes = {
  notifications: PropTypes.arrayOf(PropTypes.object),
  getUserNotifications: PropTypes.func.isRequired,
  markNotificationAsRead: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  notificationsLoading: PropTypes.bool.isRequired,
  isUserDropdownOpen: PropTypes.bool.isRequired,
  isDropdownOpen: PropTypes.bool.isRequired,
  onVisibilityChange: PropTypes.func.isRequired,
  selectedInternshipId: PropTypes.number,
};

Notification.defaultProps = {
  notifications: [],
  selectedInternshipId: null,
};

export const mapStateToProps = ({
  notifications: _notifications,
  currentUser: { data },
  selectedInternship,
}) => {
  const ability = AbilitiesService();
  let notifications;
  if (ability.can(actions.see, section.allNotifications)) {
    notifications = _notifications.data;
  } else {
    notifications = filterByInternship(
      selectedInternship && selectedInternship.pk,
      _notifications.data,
    );
  }
  return {
    notifications,
    notificationsLoading: _notifications.loading,
    role: data.role,
    selectedInternshipId: selectedInternship && selectedInternship.pk,
  };
};

export default connect(mapStateToProps, {
  getUserNotifications,
  markNotificationAsRead,
})(Notification);
